import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import {
  Stethoscope, HeartHandshake, Brain, Baby, Pill, Ear, Hand, ClipboardList,
  FlaskConical, Compass, BookOpenCheck, Search, ArrowRight, Sparkles,
} from "lucide-react";

const ICON_BY_SLUG = {
  "primeiros-sinais-de-alerta": Baby,
  "quando-e-a-quem-procurar": Compass,
  "neuropediatra": Stethoscope,
  "psicologo-infantil": HeartHandshake,
  "psiquiatra-infantil": Pill,
  "fonoaudiologo": Ear,
  "terapeuta-ocupacional": Hand,
  "avaliacao-neuropsicologica": Brain,
  "exames-complementares": FlaskConical,
  "proximos-passos-apos-o-diagnostico": ClipboardList,
};

// Paleta discreta por categoria (mantém padrão visual do site sem gritar).
// Cada entrada devolve tokens de cor para: badge fundo, badge texto, ícone fundo, ícone cor, borda superior sutil.
const CATEGORY_STYLES = {
  "Sinais e Observação": {
    dot: "bg-amber-500",
    badge: "bg-amber-50 text-amber-800 border border-amber-100",
    ring: "hover:border-amber-200",
    icon: "bg-amber-50 text-amber-700 group-hover:bg-amber-500 group-hover:text-white",
    strip: "before:bg-amber-400",
  },
  "Onde começar": {
    dot: "bg-sky-500",
    badge: "bg-sky-50 text-sky-800 border border-sky-100",
    ring: "hover:border-sky-200",
    icon: "bg-sky-50 text-sky-700 group-hover:bg-sky-500 group-hover:text-white",
    strip: "before:bg-sky-400",
  },
  "Profissionais": {
    dot: "bg-emerald-500",
    badge: "bg-emerald-50 text-emerald-800 border border-emerald-100",
    ring: "hover:border-emerald-200",
    icon: "bg-emerald-50 text-emerald-700 group-hover:bg-emerald-500 group-hover:text-white",
    strip: "before:bg-emerald-400",
  },
  "Exames e avaliações": {
    dot: "bg-violet-500",
    badge: "bg-violet-50 text-violet-800 border border-violet-100",
    ring: "hover:border-violet-200",
    icon: "bg-violet-50 text-violet-700 group-hover:bg-violet-500 group-hover:text-white",
    strip: "before:bg-violet-400",
  },
  "Após o diagnóstico": {
    dot: "bg-rose-500",
    badge: "bg-rose-50 text-rose-800 border border-rose-100",
    ring: "hover:border-rose-200",
    icon: "bg-rose-50 text-rose-700 group-hover:bg-rose-500 group-hover:text-white",
    strip: "before:bg-rose-400",
  },
  __default: {
    dot: "bg-slate-400",
    badge: "bg-slate-50 text-slate-700 border border-slate-100",
    ring: "hover:border-slate-200",
    icon: "bg-slate-50 text-slate-700 group-hover:bg-slate-500 group-hover:text-white",
    strip: "before:bg-slate-300",
  },
};

const styleFor = (category) => CATEGORY_STYLES[category] || CATEGORY_STYLES.__default;

export default function DiagnosticGuide() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/diagnostic-guide").then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, []);

  const filtered = (items || []).filter((r) => {
    const t = q.trim().toLowerCase();
    if (!t) return true;
    return (
      r.title?.toLowerCase().includes(t) ||
      r.category?.toLowerCase().includes(t) ||
      r.summary?.toLowerCase().includes(t)
    );
  });

  return (
    <div>
      <section className="relative overflow-hidden border-b border-[hsl(var(--border))] bg-gradient-to-br from-[hsl(var(--accent))]/10 via-white to-[hsl(var(--primary))]/8">
        <div className="container-cuidar py-14">
          <div className="flex flex-wrap items-center gap-2 text-xs font-semibold tracking-widest uppercase text-[hsl(var(--secondary))]">
            <Sparkles className="h-3.5 w-3.5" /> Guia do Diagnóstico
          </div>
          <h1 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-black leading-tight max-w-3xl">
            Um caminho claro para o diagnóstico do seu filho
          </h1>
          <p className="mt-5 max-w-2xl text-[hsl(var(--muted-foreground))]">
            Da primeira suspeita ao laudo — passando por cada profissional, exame e passo prático.
            Um guia acolhedor, escrito com cuidado para famílias que estão começando essa jornada.
          </p>
          <div className="mt-8 relative max-w-lg">
            <Search className="h-4 w-4 absolute left-4 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por profissional, sinal ou palavra-chave…"
              className="h-12 pl-10 rounded-full border-[hsl(var(--border))] bg-white shadow-sm"
              data-testid="diagnostic-search-input"
            />
          </div>
        </div>
      </section>

      <section className="container-cuidar py-14">
        {items === null && (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="surface p-6 animate-pulse h-52 bg-[hsl(var(--muted))]" />
            ))}
          </div>
        )}

        {items !== null && filtered.length === 0 && (
          <div className="surface p-10 text-center" data-testid="diagnostic-empty">
            <BookOpenCheck className="h-10 w-10 text-[hsl(var(--muted-foreground))] mx-auto" />
            <div className="mt-3 font-heading font-bold">Estamos preparando o conteúdo</div>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
              Nossa equipe está finalizando este guia. Em alguns instantes ele aparece aqui.
              Se você buscou um termo específico, tente outro sinônimo.
            </p>
          </div>
        )}

        {items !== null && filtered.length > 0 && (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="diagnostic-grid">
            {filtered.map((r, idx) => {
              const Icon = ICON_BY_SLUG[r.slug] || BookOpenCheck;
              const s = styleFor(r.category);
              return (
                <Link
                  key={r.id}
                  to={`/guia-do-diagnostico/${r.slug}`}
                  className={`group surface p-6 pt-7 transition-all hover:-translate-y-1 hover:shadow-lg block relative overflow-hidden border border-[hsl(var(--border))] ${s.ring}`}
                  data-testid={`diagnostic-card-${r.slug}`}
                  style={{ animation: `fadeIn 0.4s ${idx * 40}ms both` }}
                >
                  <span className={`absolute top-0 left-0 right-0 h-[3px] ${s.dot}`} aria-hidden="true" />
                  <div className="flex items-start gap-4">
                    <div className={`shrink-0 h-12 w-12 rounded-2xl grid place-items-center transition-colors ${s.icon}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-bold ${s.badge}`}>
                        <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
                        {r.category}
                      </span>
                      <div className="mt-2 font-heading font-extrabold text-lg leading-tight">
                        {r.title}
                      </div>
                    </div>
                  </div>
                  <p className="mt-4 text-sm text-[hsl(var(--muted-foreground))] line-clamp-3">
                    {r.summary}
                  </p>
                  <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[hsl(var(--primary))]">
                    Ler guia completo <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        <div className="mt-14 rounded-3xl bg-gradient-to-br from-[hsl(var(--primary))] to-[hsl(var(--primary))]/85 text-white p-8 md:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-widest text-white/70">Sua família não caminha sozinha</div>
            <div className="mt-2 font-heading text-2xl font-black">Converse com quem já viveu isso</div>
            <p className="text-sm mt-2 text-white/80 max-w-xl">
              Além do guia, nossa comunidade e nossa equipe estão prontas para acolher você em cada
              etapa da investigação diagnóstica.
            </p>
          </div>
          <a
            href="/contato"
            className="btn-pill inline-flex items-center gap-2 bg-white text-[hsl(var(--primary))] px-6 py-3 font-bold hover:bg-white/95"
            data-testid="diagnostic-cta-contato"
          >
            Falar com a equipe <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </section>
    </div>
  );
}
